/**
 * Die EINE Regel, welcher Satz bei welcher Datenlage ueber die eigene Kundenhistorie erscheint.
 *
 * Bauplan: docs/KONZEPT-RECHERCHE-SICHTBARKEIT.md § 3.2 (die eigene Historie ist der wertvollste
 * Datensatz und wird heute nirgends erzaehlt), § 2.5 (im Kunden-Profil steht sie als ERSTER Block,
 * vor allen Fremddaten), § 6.1 (beschreibend, nie bewertend). Entscheidung P-7: die eigene
 * Erfahrung schlaegt jede Registerauskunft. Kriterien R1.2 (Anfragen-Drawer) und R1.3
 * (Kunden-Profil) in docs/LOOP-RECHERCHE.md.
 *
 * ── Warum EIN Paket und nicht zwei Komponenten ────────────────────────────────────────────────
 * Dieselbe Historie erscheint an ZWEI Orten. Wuerde jeder Ort seine Saetze selbst formulieren,
 * saehe der Betreiber fuer denselben Kunden zwei Darstellungen — und beide saehen fuer sich
 * plausibel aus. Genau diese Doppelung wurde bei der Deckungsregel (§ 5.9) und beim Stand
 * (R1.6, `@maestro/recherche-stand`) schon einmal abgeschafft; hier wird sie gar nicht erst
 * angelegt: EINE reine Funktion, zwei Aufrufer, die nur noch rendern.
 *
 * `apps/web` hat KEINEN Test-Runner (in R0.2 festgehalten, in R0.7/R0.9/R1.6 erneut). Eine Regel
 * wie „bei dieser Datenlage steht dort ausdruecklich ‚Neukunde‘ und keine leere Flaeche" waere in
 * einer React-Komponente unpruefbar. Sie wohnt deshalb hier; die Tests liegen in
 * `apps/api/test/kundenhistorie-anzeige.test.ts`, weil genau diese Suite in der CI garantiert
 * mitlaeuft. Dieses Paket hat darum bewusst KEIN `test`-Skript (die Ratsche
 * `apps/api/test/ci-testabdeckung.test.ts` verlangt fuer jedes Paket MIT `test`-Skript einen
 * eigenen CI-Aufruf — ein zusaetzliches Ziel ohne zusaetzliche Sicherheit).
 *
 * ── Beschreiben, nicht bewerten (§ 6.1) ───────────────────────────────────────────────────────
 * Hier entsteht KEINE Quote, KEIN Prozentzeichen, KEIN Score, KEINE Ampel, KEINE Prognose und
 * kein Satz wie „zuverlaessiger Kunde". Es gibt nur gezaehlte Vergangenheit: „2 von 3 Rechnungen
 * puenktlich bezahlt" statt „zahlt puenktlich", „1 versendetes Angebot (1 angenommen)" statt einer
 * Annahmequote. Der Wortlaut ist woertlich derselbe wie im Angebots-Briefing (R1.4,
 * `apps/api/src/lib/offer-briefing.ts`) — dieselbe Historie darf im Chat, im Prompt und auf dem
 * Bildschirm nicht drei Sprachen sprechen. Eine Wortlaut-Ratsche im Test faehrt ueber die
 * ERZEUGTEN Saetze (nicht ueber den Quelltext, sonst faenge sie diesen Kommentar).
 *
 * ── Warum hier KEIN „Stand vom …" steht (R1.6) ────────────────────────────────────────────────
 * Die Ratsche aus R1.6 erkennt eine Recherche-Angabe an zwei Wurzeln: die Nutzlast fuehrt
 * `lookedUpAt` (der Lebenszyklus-Vertrag aus § 4.1), oder ihr Doc-Kommentar nennt die
 * Hausbezeichnung der Anreicherungs-Wellen. Diese Nutzlast fuehrt kein `lookedUpAt` und ist keine
 * Anreicherung — und das ist kein Schlupfloch, sondern der Sachverhalt: die Historie ruft nichts
 * ab. Sie ZAEHLT im Moment der Anfrage, was in der eigenen Datenbank steht. Ihr Stand ist damit
 * immer der Augenblick des Aufrufs; „Stand heute" an jeder Zeile waere eine Zeile ohne Aussage.
 * Was die Angaben stattdessen brauchen, ist ihr BEZUG — worueber gerade gezaehlt wird (Firma oder
 * E-Mail-Adresse) —, und den gibt `bezugSatz` aus. Die Ratsche bleibt davon unberuehrt; der Test
 * haelt das ausdruecklich fest, damit niemand spaeter raten muss.
 *
 * ── Betraege ─────────────────────────────────────────────────────────────────────────────────
 * Diese Regel gibt KEINEN Betrag aus, obwohl die Nutzlast Summe und Durchschnitt mitbringt. Grund
 * ist nicht § 2.6 (dort geht es um den Modell-Prompt, hier liest ein Mensch), sondern die
 * Widerspruchsfreiheit: das Kunden-Profil zeigt oben bereits die Kacheln „Umsatz" und
 * „Ø Auftragswert", und die rechnen ueber eine ANDERE Grundmenge (alle Vorgaenge bzw. nur
 * `paid`/`completed`) als `auftragswert` hier (gebuchte Vorgaenge mit hinterlegtem Betrag). Zwei
 * gleich beschriftete Zahlen auf einer Seite waeren zwei Wahrheiten, von denen jede fuer sich
 * plausibel aussieht. Die Felder bleiben im Typ — sie sind Anzeige-Material, nie Eingabe fuer ein
 * Angebot —, aber diese Regel setzt sie in keinen Satz.
 *
 * ── Reinheit ─────────────────────────────────────────────────────────────────────────────────
 * Kein `new Date()`, kein `Intl`, kein Zugriff auf irgendeine Umgebung: aus Zahlen werden Saetze,
 * sonst nichts. Dieselbe Eingabe ergibt in Berlin, Kolkata und in der CI dasselbe Ergebnis.
 */

/* ────────────────────────────── Eingabe ────────────────────────────────────────────────────── */

/** Worauf sich die Historie bezieht — die Regel dazu trifft `lib/kundenhistorie.ts` (R1.1). */
export type HistorieBezugArt = "firma" | "kontakt" | "unbekannt";

/**
 * `data` von `GET /api/inquiries/:id/historie` bzw. `GET /api/customers/:id/historie` (R1.1).
 *
 * Bewusst hier nachgebildet statt aus `apps/api` importiert: ein Anzeige-Paket, das ins Backend
 * greift, waere im Browser-Bundle ein Fremdkoerper. Der Typ ist strukturell deckungsgleich mit
 * `Kundenhistorie`; der Test uebergibt die echte Serverantwort an diese Funktion und faellt damit
 * auf, sobald die beiden auseinanderlaufen.
 */
export interface HistorieNutzlast {
  bezug: { art: HistorieBezugArt; wert: string | null };
  /** Nie gearbeitet mit diesem Bezug: kein versendetes Angebot, nichts gebucht/storniert/bezahlt. */
  istNeukunde: boolean;
  anfragen: { gesamt: number; ersteAm: string | null; letzteAm: string | null };
  angebote: { versendet: number; angenommen: number; abgelehnt: number; offen: number };
  veranstaltungen: { gebucht: number; durchgefuehrt: number; storniert: number };
  auftragswert: { gebuchteMitBetrag: number; summeCents: number; durchschnittCents: number | null };
  zahlungen: {
    beurteilbar: number;
    puenktlich: number;
    verspaetet: number;
    offen: number;
    ueberfaellig: number;
    imSchnittTageZurFaelligkeit: number | null;
  };
}

/**
 * Der Ort, an dem der Block steht. Er entscheidet AUSSCHLIESSLICH ueber die Ueberschrift — die
 * Saetze sind an beiden Orten Wort fuer Wort dieselben. Zwei Ueberschriften, weil an beiden Orten
 * eine andere Frage im Raum steht: auf der Anfragen-Detailseite die Frage „Kennen wir schon?"
 * (der Betreiber sieht eine Anfrage und weiss noch nicht, wer da schreibt), im Kunden-Profil ist
 * die Person laengst bekannt und es geht um das gemeinsam Erlebte.
 *
 * Umbau 27.08.2026: "anfragen_drawer" -> "anfragen_detail", weil der Detail-Drawer einer eigenen
 * Route gewichen ist (`pages/anfrage-detail.tsx`) — derselbe Ort inhaltlich, andere Bauart.
 */
export type HistorieOrt = "anfragen_detail" | "kunden_profil";

export interface HistorieAnzeigeLage {
  /** Die geladene Historie — `null` heisst: noch nichts da (Ladezustand, Fehler, andere Anfrage). */
  historie: HistorieNutzlast | null;
  ort: HistorieOrt;
}

/* ────────────────────────────── Ausgabe ────────────────────────────────────────────────────── */

/**
 * Die drei Datenlagen. Ausdruecklich DREI und nicht zwei: „Neukunde" und „kein Bezug" sehen in den
 * Zahlen identisch aus (ueberall null), sind aber zwei verschiedene Aussagen. „Neukunde" heisst:
 * wir haben nachgesehen und nichts gefunden. „Nicht zuzuordnen" heisst: es gab nichts, wonach wir
 * haetten suchen koennen. Die beiden zu verschmelzen hiesse, eine Wissensluecke als Wissen
 * auszugeben — genau der Fehler, den dieser Loop abschafft.
 */
export type HistorieDatenlage = "wiederkehrend" | "neukunde" | "kein_bezug";

export interface KundenhistorieAnzeige {
  /** `false` = der Block wird gar nicht gerendert (noch nichts geladen). */
  sichtbar: boolean;
  /** Ueberschrift — je Ort eine, siehe `HistorieOrt`. */
  titel: string;
  datenlage: HistorieDatenlage;
  /**
   * Der EINE Satz im Kopf: „Wiederkehrender Kunde", „Neukunde", „Nicht zuzuordnen".
   *
   * Er ist der Kern von R1.2: ein wiederkehrender Kunde muss als solcher ERKENNBAR sein, ein
   * Neukunde bekommt ausdruecklich das Wort — nicht eine leere Flaeche.
   */
  kurzinfo: string;
  /** Die gezaehlten Tatsachen, je eine Zeile. Beim Neukunden hoechstens die Anfragen. */
  zeilen: string[];
  /**
   * Der erklaerende Satz unter den Zeilen. NIE `null` — ein Block ohne Aussage waere die leere
   * Flaeche, die R1.2 ausdruecklich verbietet.
   */
  fussnote: string;
  /**
   * Worueber gezaehlt wurde („Gezählt über die Firma …"). `null` nur, wenn es keinen Bezug gibt —
   * dann sagt das bereits die `fussnote`.
   */
  bezugSatz: string | null;
}

/* ────────────────────────────── Beschriftungen ─────────────────────────────────────────────── */

/** Ueberschrift auf der Anfragen-Detailseite — der Wortlaut aus Kriterium R1.2. */
export const TITEL_ANFRAGE = "Kennen wir schon?";
/** Ueberschrift im Kunden-Profil (§ 3.2: „Was wir mit dieser Firma erlebt haben"). */
export const TITEL_KUNDE = "Was wir erlebt haben";

export const KURZINFO_WIEDERKEHREND = "Wiederkehrender Kunde";
export const KURZINFO_NEUKUNDE = "Neukunde";
export const KURZINFO_KEIN_BEZUG = "Nicht zuzuordnen";

/* ────────────────────────────── Grammatik ──────────────────────────────────────────────────── */

/**
 * „1 Rechnung" / „2 Rechnungen". Woertlich dieselbe Hilfsfunktion wie in `lib/offer-briefing.ts`
 * (R1.4) — bewusst kopiert und nicht importiert: ein Anzeige-Paket, das eine Backend-Datei
 * importiert, zoege den halben Server ins Browser-Bundle. Dass beide Seiten dieselben Saetze
 * bilden, sichert der Test (`kundenhistorie-anzeige.test.ts` vergleicht die Wortbausteine mit
 * denen des Briefings), nicht die Hoffnung.
 */
function einzahlMehrzahl(n: number, einzahl: string, mehrzahl: string): string {
  return `${n} ${n === 1 ? einzahl : mehrzahl}`;
}

/* ────────────────────────────── Die Regel ──────────────────────────────────────────────────── */

/**
 * Baut den Historien-Block aus der Antwort von R1.1.
 *
 * Reihenfolge der Entscheidungen (jede hat einen Grund):
 *  1. Nichts geladen ⇒ kein Block. Ein Kasten, der behauptet „Neukunde", weil die Antwort noch
 *     unterwegs ist, waere schlimmer als kein Kasten.
 *  2. Kein Bezug (weder Firma noch E-Mail) ⇒ eigener Zustand. NICHT „Neukunde": wir haben nicht
 *     nachgesehen, wir konnten es nicht.
 *  3. `istNeukunde` ⇒ das Wort „Neukunde" plus die Begruendung, die exakt die Definition aus
 *     `lib/kundenhistorie.ts` wiederholt. Die Anfragen erscheinen trotzdem, denn sie SIND Historie
 *     — sie machen nur niemanden zum Bestandskunden.
 *  4. Sonst ⇒ „Wiederkehrender Kunde" und die gezaehlten Zeilen, in der Reihenfolge des Briefings
 *     (R1.4): gebuchte Veranstaltungen, versendete Angebote, Stornos, Zahlungen. Danach die beiden
 *     Zeilen, die es nur auf dem Bildschirm gibt (noch nicht faellige Rechnungen, Anfragen) — im
 *     Prompt sind sie Ballast, vor einem Menschen sind sie Kontext.
 */
export function kundenhistorieAnzeige(lage: HistorieAnzeigeLage): KundenhistorieAnzeige {
  const titel = lage.ort === "anfragen_detail" ? TITEL_ANFRAGE : TITEL_KUNDE;
  const h = lage.historie;

  // 1. Noch nichts da.
  if (!h) {
    return {
      sichtbar: false,
      titel,
      datenlage: "kein_bezug",
      kurzinfo: "",
      zeilen: [],
      fussnote: "",
      bezugSatz: null,
    };
  }

  const bezugSatz = bezugSatzBauen(h.bezug);

  // 2. Es gibt nichts, woran eine Historie haengen koennte.
  if (h.bezug.art === "unbekannt") {
    return {
      sichtbar: true,
      titel,
      datenlage: "kein_bezug",
      kurzinfo: KURZINFO_KEIN_BEZUG,
      zeilen: [],
      fussnote: "Ohne Firmennamen und ohne E-Mail-Adresse gibt es nichts, woran eine Historie hängen könnte.",
      bezugSatz: null,
    };
  }

  const anfragenZeile =
    h.anfragen.gesamt > 0 ? `${einzahlMehrzahl(h.anfragen.gesamt, "Anfrage", "Anfragen")} insgesamt` : null;

  // 3. Neukunde — ein Zustand, keine leere Flaeche (R1.2, woertlich).
  if (h.istNeukunde) {
    return {
      sichtbar: true,
      titel,
      datenlage: "neukunde",
      kurzinfo: KURZINFO_NEUKUNDE,
      zeilen: anfragenZeile ? [anfragenZeile] : [],
      // Exakt die Definition aus lib/kundenhistorie.ts — keine zweite Auslegung von „neu".
      fussnote: "Noch kein versendetes Angebot, keine Buchung, kein Storno und keine Rechnung.",
      bezugSatz,
    };
  }

  // 4. Wiederkehrender Kunde — gezaehlte Vergangenheit, Zeile fuer Zeile.
  const zeilen: string[] = [];

  if (h.veranstaltungen.gebucht > 0) {
    zeilen.push(einzahlMehrzahl(h.veranstaltungen.gebucht, "gebuchte Veranstaltung", "gebuchte Veranstaltungen"));
  }

  if (h.angebote.versendet > 0) {
    // Die Klammer nennt IMMER die angenommenen — auch „0 angenommen". Weglassen hiesse: das
    // Fehlen sieht aus wie ein fehlendes Feld statt wie eine gezaehlte Null.
    const beantwortet = [`${h.angebote.angenommen} angenommen`];
    if (h.angebote.abgelehnt > 0) beantwortet.push(`${h.angebote.abgelehnt} abgelehnt`);
    if (h.angebote.offen > 0) beantwortet.push(`${h.angebote.offen} noch offen`);
    zeilen.push(
      `${einzahlMehrzahl(h.angebote.versendet, "versendetes Angebot", "versendete Angebote")} (${beantwortet.join(", ")})`,
    );
  }

  if (h.veranstaltungen.storniert > 0) {
    zeilen.push(einzahlMehrzahl(h.veranstaltungen.storniert, "Storno", "Stornos"));
  }

  if (h.zahlungen.beurteilbar > 0) {
    // „Puenktlich" ist hier exakt das, was lib/kundenhistorie.ts gezaehlt hat: am Faelligkeitstag
    // oder davor gezahlt, Zeitzone Europe/Berlin. Keine zweite Definition, und keine Quote:
    // „2 von 3" ist dieselbe Information wie „67 %" — ohne die Suggestion einer Kennziffer.
    zeilen.push(
      `${h.zahlungen.puenktlich} von ${einzahlMehrzahl(h.zahlungen.beurteilbar, "Rechnung", "Rechnungen")} pünktlich bezahlt`,
    );
  }
  if (h.zahlungen.ueberfaellig > 0) {
    zeilen.push(`${einzahlMehrzahl(h.zahlungen.ueberfaellig, "Rechnung", "Rechnungen")} überfällig`);
  }
  if (h.zahlungen.offen > 0) {
    zeilen.push(`${einzahlMehrzahl(h.zahlungen.offen, "Rechnung", "Rechnungen")} noch nicht fällig`);
  }

  if (anfragenZeile) zeilen.push(anfragenZeile);

  return {
    sichtbar: true,
    titel,
    datenlage: "wiederkehrend",
    kurzinfo: KURZINFO_WIEDERKEHREND,
    zeilen,
    // Der Satz, der die ganze Karte einordnet — und die einzige Stelle, an der ausgesprochen wird,
    // was sie NICHT ist. Ohne ihn liest sich eine Liste von Zahlungszeilen wie eine Einschaetzung.
    fussnote: "Gezählte Vergangenheit aus der eigenen Datenbank — keine Bewertung und keine Aussage über künftiges Zahlen.",
    bezugSatz,
  };
}

/**
 * Worueber gezaehlt wurde. Die Angabe ist kein Beiwerk: dieselbe Firma kann unter zwei
 * E-Mail-Adressen schreiben, und dieselbe Adresse kann fuer zwei Firmen schreiben. Wer die Zahlen
 * liest, muss wissen, welcher Schluessel sie erzeugt hat (R1.1 liefert ihn genau dafuer mit).
 */
function bezugSatzBauen(bezug: HistorieNutzlast["bezug"]): string | null {
  const wert = bezug.wert?.trim();
  if (!wert) return null;
  if (bezug.art === "firma") return `Gezählt über die Firma „${wert}“.`;
  if (bezug.art === "kontakt") return `Gezählt über die E-Mail-Adresse ${wert}.`;
  return null;
}
